import {
	type ActivityFilters,
	type ActivityPage,
	BULK_USERS_MAX,
	type BulkRevokeSessionsBody,
	type BulkRevokeSessionsResult,
	type BulkStatusBody,
	type BulkStatusResult,
	type CreateUserBody,
	type CreatedUser,
	type ListUsersQuery,
	type LoginAttemptsPage,
	type SignInLogApp,
	type SignInLogPage,
	type Paginated,
	type ResetPasswordBody,
	type ResetPasswordResult,
	type RoleCode,
	type RoleItem,
	type ScopeOptions,
	type SessionItem,
	type SetUserGroupBody,
	type UpdateUserBody,
	type UserDetail,
	type UserListItem,
	type UserStatus,
} from "@seagames/contracts";
import { infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { api } from "../api/client";
import { queryClient } from "../api/query-client";

export const usersKeys = {
	all: ["users"] as const,
	list: (q: ListUsersQuery) => ["users", "list", q] as const,
	detail: (id: string) => ["users", "detail", id] as const,
};

/** One request → page + total + every row's roles (the API aggregates in SQL). */
export const usersListOptions = (q: ListUsersQuery) =>
	queryOptions({
		queryKey: usersKeys.list(q),
		queryFn: ({ signal }) => api.get<Paginated<UserListItem>>("/users", { query: q, signal }),
		// Keep the previous page on screen while the next loads — no blank flash.
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

/** One account, fresh — the edit form opens on this, not on a possibly-stale list row. */
export const userOptions = (id: string) =>
	queryOptions({
		queryKey: usersKeys.detail(id),
		queryFn: ({ signal }) => api.get<UserDetail>(`/users/${id}`, { signal }),
	});

/** `GET /users/scope-options` — orgs, functions, sub-agencies, games for the group scope editor. Reference data; cache hard. */
export const scopeOptionsOptions = () =>
	queryOptions({
		queryKey: ["users", "scope-options"] as const,
		queryFn: ({ signal }) => api.get<ScopeOptions>("/users/scope-options", { signal }),
		staleTime: 5 * 60_000,
	});

/** `GET /roles` — small, slow-changing; the role picker reads it. */
export const rolesListOptions = () =>
	queryOptions({
		queryKey: ["roles"] as const,
		queryFn: ({ signal }) => api.get<RoleItem[]>("/roles", { signal }),
		staleTime: 5 * 60_000,
	});

export const invalidateUsers = () => queryClient.invalidateQueries({ queryKey: usersKeys.all });

// ── Single-user writes (SUPER_ADMIN) ────────────────────────────────────────
// Each is its own endpoint on purpose: roles and status revoke sessions,
// reset-password forces a change — they are separate audit events.

export const createUser = (body: CreateUserBody) => api.post<CreatedUser>("/users", body);

export const updateUser = (id: string, body: UpdateUserBody) => api.patch<UserListItem>(`/users/${id}`, body);

export const setUserRoles = (id: string, roles: readonly RoleCode[]) =>
	api.patch<UserListItem>(`/users/${id}/roles`, { roles });

/**
 * Put the login in a group (or `null` = take it out). Takes effect on the
 * target's next request. 422 GROUP_ROLE_MISMATCH when the login does not hold
 * the group's role.
 */
export const setUserGroup = (id: string, body: SetUserGroupBody) => api.put<UserDetail>(`/users/${id}/group`, body);

export const setUserStatus = (id: string, status: UserStatus) =>
	api.patch<UserListItem>(`/users/${id}/status`, { status });

/** No `new_password` → the server generates one and returns it exactly once. Always needs the admin's own password. */
export const resetUserPassword = (id: string, body: ResetPasswordBody) =>
	api.post<ResetPasswordResult>(`/users/${id}/reset-password`, body);

// ── Bulk writes (SUPER_ADMIN, confirmed with the admin's own password) ──────
// One request for the whole selection: one transaction, one audit batch, one
// password check. A wrong password is 403 CONFIRMATION_PASSWORD_INVALID and
// nothing changes; the caller's own id is 403 CANNOT_TARGET_SELF.

/**
 * The selection survives paging (`useRowSelection`), so it CAN exceed one
 * request's cap even though a page holds at most 100 rows. Larger selections
 * go as ≤ BULK_USERS_MAX-id requests, one after another with the same
 * password — deliberately sequential, not a JOIN we forgot: a wrong password
 * then fails once, on the first request, before anything changed, instead of
 * burning N attempts of the change-password limiter in parallel. If a later
 * chunk fails, earlier ones HAVE applied; callers refresh the list either way.
 */
function chunked<T>(items: readonly T[]): T[][] {
	const out: T[][] = [];
	for (let i = 0; i < items.length; i += BULK_USERS_MAX) out.push(items.slice(i, i + BULK_USERS_MAX));
	return out;
}

export async function setUsersStatus(ids: readonly string[], status: UserStatus, confirmPassword: string): Promise<BulkStatusResult> {
	const total: BulkStatusResult = { updated_ids: [], sessions_revoked: 0 };
	for (const part of chunked(ids)) {
		const body: BulkStatusBody = { ids: part, status, confirm_password: confirmPassword };
		const r = await api.post<BulkStatusResult>("/users/bulk/status", body);
		total.updated_ids.push(...r.updated_ids);
		total.sessions_revoked += r.sessions_revoked;
	}
	return total;
}

/** "Sign out everywhere" for each id — also the single-user action on the detail page (`[id]`). */
export async function revokeUsersSessions(ids: readonly string[], confirmPassword: string): Promise<BulkRevokeSessionsResult> {
	const total: BulkRevokeSessionsResult = { user_ids: [], sessions_revoked: 0 };
	for (const part of chunked(ids)) {
		const body: BulkRevokeSessionsBody = { ids: part, confirm_password: confirmPassword };
		const r = await api.post<BulkRevokeSessionsResult>("/users/bulk/revoke-sessions", body);
		total.user_ids.push(...r.user_ids);
		total.sessions_revoked += r.sessions_revoked;
	}
	return total;
}

/**
 * Unlock has no bulk endpoint (it only clears limiter counters, no password
 * asked), so it fans out per id with bounded concurrency — never `Promise.all`
 * over an unbounded list (CLAUDE.md Rule 0).
 */
const CONCURRENCY = 4;

async function forEachBounded<T>(items: readonly T[], fn: (item: T) => Promise<unknown>) {
	const failures: Array<{ item: T; error: unknown }> = [];
	let i = 0;
	const worker = async () => {
		while (i < items.length) {
			const item = items[i++]!;
			try {
				await fn(item);
			} catch (error) {
				failures.push({ item, error });
			}
		}
	};
	await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
	return failures;
}

export const unlockUsers = (ids: readonly string[]) => forEachBounded(ids, (id) => api.post(`/users/${id}/unlock`));

// ── Account detail ──────────────────────────────────────────────────────────

export const userDetailKeys = {
	activity: (id: string, scope: "actor" | "subject", filters: ActivityFilters) =>
		["users", "detail", id, "activity", scope, filters] as const,
	loginAttempts: (id: string, page: number) => ["users", "detail", id, "login-attempts", page] as const,
	sessions: (id: string) => ["users", "detail", id, "sessions"] as const,
};

/** Keyset: each page hands back `next_cursor`; `useInfiniteQuery` threads it into the next request. */
export const userActivityOptions = (id: string, scope: "actor" | "subject", filters: ActivityFilters = {}, pageSize = 10) =>
	infiniteQueryOptions({
		queryKey: userDetailKeys.activity(id, scope, filters),
		queryFn: ({ pageParam, signal }) =>
			api.get<ActivityPage>(`/users/${id}/activity`, {
				query: { scope, page_size: pageSize, ...filters, ...(pageParam ? { cursor: pageParam } : {}) },
				signal,
			}),
		initialPageParam: null as string | null,
		getNextPageParam: (last) => last.next_cursor,
		staleTime: 15_000,
	});

export const userLoginAttemptsOptions = (id: string, page = 1, pageSize = 20) =>
	queryOptions({
		queryKey: userDetailKeys.loginAttempts(id, page),
		queryFn: ({ signal }) =>
			api.get<LoginAttemptsPage>(`/users/${id}/login-attempts`, { query: { page, page_size: pageSize }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/**
 * The sign-in log across every login (SUPER_ADMIN, `GET /login-attempts`):
 * keyset, newest first, filtered by app and outcome. "Load more" fetches the
 * next page by cursor — never an offset into a log that only grows.
 */
export const signInLogOptions = (filter: { app?: SignInLogApp | undefined; success?: "true" | "false" | undefined }) =>
	infiniteQueryOptions({
		queryKey: ["users", "sign-in-log", filter.app ?? null, filter.success ?? null] as const,
		queryFn: ({ signal, pageParam }) => {
			const query: Record<string, string> = { limit: "50" };
			if (filter.app) query.app = filter.app;
			if (filter.success) query.success = filter.success;
			if (pageParam) query.cursor = pageParam;
			return api.get<SignInLogPage>("/login-attempts", { query, signal });
		},
		initialPageParam: null as string | null,
		getNextPageParam: (last) => last.next_cursor,
		staleTime: 15_000,
	});

/** SUPER_ADMIN only — `/sessions` is gated server-side. */
export const userSessionsOptions = (id: string) =>
	queryOptions({
		queryKey: userDetailKeys.sessions(id),
		queryFn: ({ signal }) =>
			api.get<Paginated<SessionItem>>("/sessions", { query: { user_id: id, page: 1, page_size: 50 }, signal }),
		staleTime: 15_000,
	});

export const unlockUser = (id: string) => api.post<{ unlocked: boolean }>(`/users/${id}/unlock`);
